import { useState, useEffect } from "react";
import useAuth from "../../hooks/useAuth";
import UseAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { RefreshCw, Eye, EyeOff } from "lucide-react";
import { ROLE_MANAGEMENT } from "../../utils/ApiUrls";
import { set } from "date-fns";

const AddUser = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { id } = useParams();
  const user_id = auth.user_details.id;
  const axiosprivate = UseAxiosPrivate();
  const controller = new AbortController();
  const [isLoading, setIsLoading] = useState(false);

  const isEditMode = parseInt(id) !== 0;

  const [inputs, setInputs] = useState({});
  const [userType, setUserType] = useState("agent");
  const isAdminType = userType === "admin";
  const [tempVsipPass, setTempVsipPass] = useState(null);
  const [webrtc, setWebRtc] = useState(false);
  const [feedbackRequiredBeforeNextCall, setFeedbackRequiredBeforeNextCall] = useState(false);
  const [autoAnswer, setAutoAnswer] = useState(false);
  const [autoAnswerInterval, setAutoAnswerInterval] = useState(3);
  const [showPassword, setShowPassword] = useState(false);
  const [required, setRequired] = useState({
    username: false,
    password: false,
    phone_number: false,
    call_option: false,
    call_mode: false,
    vsipobj_pass: false,
    user_role: false,
  });


  const [click, setClick] = useState({
    username: false,
    password: false,
    phone_number: false,
    call_option: false,
    call_mode: false,
    vsipobj_pass: false,
  });

  const [did, setDid] = useState([])
  const fetDIDData = async () => {
    const response = await axiosprivate.get(
      "/DIDListById/" + user_id + "/?project=contact_center",
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth?.accessToken}`,
        },
        withCredentials: true,
      }
    );
    setDid(response.data);
  };

  const [roles, setRoles] = useState([]);
  const fetchRoles = async () => {
    try {
      const response = await axiosprivate.get(ROLE_MANAGEMENT, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth?.accessToken}`,
        },
        withCredentials: true,
      });
      setRoles(response.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const [error, setError] = useState("");

  const applyFetchedUser = (data) => {
    setInputs(data);
    setTempVsipPass(data?.vsipobj_pass);
    setWebRtc(data?.webrtc);
    setAutoAnswer(data?.autoAnswer);
    setAutoAnswerInterval(data?.autoAnswerSeconds);
    setFeedbackRequiredBeforeNextCall(data?.feedback_required_before_next_call);
    if (data?.autoAnswerInterval) {
      setAutoAnswerInterval(data.autoAnswerInterval);
    }
  };

  const fetchUserDetails = async (params) => {
    const res = await axios.post(
      process.env.REACT_APP_API_URL + "/contactcentre/GetAgentUser/",
      params,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth?.accessToken}`,
        },
        withCredentials: true,
      }
    );
    applyFetchedUser(res.data);
  };

  // ---------- Fetch existing agent ONLY in edit mode ----------
  useEffect(() => {
    if (!isEditMode) return; // 🚫 don't fetch for new agent
    fetchUserDetails({ user_id, agent_id: parseInt(id) }).catch((err) =>
      console.error(err)
    );
  }, [isEditMode, id]);

  // ---------- Auto-fill Admin Name & Phone Number from auth details when User Type = Admin ----------
  // ---------- Clear those fields when switching back to Agent ----------
  useEffect(() => {
    if (isAdminType) {
      setInputs((prev) => ({
        ...prev,
        agent_username: auth?.user_details?.username || "",
        phone_number: (auth?.user_details?.mobile || "").replace(/\D/g, "").slice(-10),
      }));
    } else {
      setInputs((prev) => ({
        ...prev,
        agent_username: "",
        phone_number: "",
      }));
    }
  }, [isAdminType]);

  const Validation = (name) => {
    const map = {
      agent_username: "username",
      agent_password: "password",
      phone_number: "phone_number",
    };
    if (map[name]) {
      setClick((prev) => ({ ...prev, [map[name]]: true }));
    }
  };

  // ---------- Validation ----------
  useEffect(() => {
    if (!inputs.agent_username && click.username) {
      setRequired((p) => ({ ...p, username: true }));
    } else {
      setRequired((p) => ({ ...p, username: false }));
    }

    if (!inputs.agent_password && click.password) {
      setRequired((p) => ({ ...p, password: true }));
    } else {
      setRequired((p) => ({ ...p, password: false }));
    }

    if (!inputs.phone_number && click.phone_number) {
      setRequired((p) => ({ ...p, phone_number: true }));
      setError("*Please Enter Phone Number");
    } else {
      const mobileNumberRegex = /^[0-9]{10}$/;
      if (mobileNumberRegex.test(inputs.phone_number)) {
        setRequired((p) => ({ ...p, phone_number: false }));
      } else if (click.phone_number) {
        setRequired((p) => ({ ...p, phone_number: true }));
        setError("*Please Enter Valid Phone Number");
      }
    }
  }, [inputs, click]);

  // ---------- VSIP password auto-gen ----------
  useEffect(() => {
    if (inputs.call_mode === "sip") {
      if (!inputs.vsipobj_pass) {
        if (!tempVsipPass) {
          generatePassword();
        } else {
          setInputs((prev) =>
            prev.vsipobj_pass === tempVsipPass
              ? prev
              : { ...prev, vsipobj_pass: tempVsipPass }
          );
        }
      }
    } else if (inputs.call_mode === "mobile") {
      setInputs((prev) =>
        prev.vsipobj_pass === null ? prev : { ...prev, vsipobj_pass: null }
      );
    }
  }, [inputs.call_mode]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const generatePassword = () => {
    const allowedspec = ["!", "#", "$", "&"];
    const allowednumber = "0123456789";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const length = 8;

    const randomFrom = (src) => src[Math.floor(Math.random() * src.length)];
    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const passwordChars = [
      randomFrom(uppercase),
      randomFrom(lowercase),
      randomFrom(allowednumber),
      randomFrom(allowedspec),
    ];

    const allChars =
      lowercase + uppercase + allowednumber + allowedspec.join("");

    while (passwordChars.length < length) {
      passwordChars.push(randomFrom(allChars));
    }

    const generatedPassword = shuffle(passwordChars).join("");
    setInputs((prev) => ({ ...prev, vsipobj_pass: generatedPassword }));
  };

  const handleSubmit = async (event, confirmed = "no") => {
    event.preventDefault();
    setIsLoading(true);

    setRequired((p) => ({
      ...p,
      call_option: !inputs.call_option,
      call_mode: !inputs.call_mode,
      vsipobj_pass: inputs.call_mode === "sip" && !inputs.vsipobj_pass,
      user_role: !inputs.user_role,
    }));

    let warningText = "";
    if ((inputs.agent_username || "").includes(" ")) {
      warningText =
        "Spaces are not allowed in Username. You can use an underscore (_) in place of spaces.";
    } else if ((inputs.agent_password || "").includes(" ")) {
      warningText =
        "Spaces are not allowed in Password. You can use an underscore (_) in place of spaces.";
    } else if ((inputs.phone_number || "").includes(" ")) {
      warningText = "Spaces are not allowed in Phone Number. Please remove.";
    }
    if (/[a-zA-Z]/.test(inputs.phone_number || "")) {
      warningText = "Alphabets are not allowed in Phone Number. Please Remove.";
    }
    if (warningText) {
      Swal.fire({
        title: "Invalid Input",
        text: warningText,
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        icon: "warning",
      });
      setIsLoading(false);
      return;
    }

    if (
      inputs.call_mode === "mobile" &&
      (inputs.phone_number || "").trim().length !== 10
    ) {
      Swal.fire({
        title: "Invalid Input",
        text: "Phone Number Should be 10 digit.",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        icon: "warning",
      });
      setIsLoading(false);
      return;
    }

    if (webrtc && autoAnswer) {
      const intervalNum = Number(autoAnswerInterval);
      if (!Number.isInteger(intervalNum) || intervalNum < 1 || intervalNum > 60) {
        Swal.fire({
          title: "Invalid Input",
          text: "Auto Answer Interval must be a whole number between 1 and 60 seconds.",
          icon: "warning",
        });
        setIsLoading(false);
        return;
      }
    }

    const post_data = {
      userId: user_id,
      user_type: userType,
      ...(isAdminType ? { admin_id: user_id } : { agent_id: id }),
      agent_username: inputs.agent_username,
      agent_password: inputs.agent_password,
      call_mode: inputs.call_mode,
      call_option: inputs.call_option,
      phone_number: inputs.phone_number,
      vsipobj_pass: inputs.vsipobj_pass,
      did: inputs.did,
      user_role: inputs.user_role,
      is_confirmed_for_profile_save: confirmed,
      webrtc,
      autoAnswer,
      autoAnswerInterval: webrtc && autoAnswer ? Number(autoAnswerInterval) : 0,
      login_activation: inputs.login_activation || "Allowed",
      feedback_required_before_next_call: feedbackRequiredBeforeNextCall,
    };

    const isAnyValueUndefined = Object.entries(post_data).some(
      ([k, v]) =>
        k !== "autoAnswerInterval" &&
        k !== "vsipobj_pass" &&
        (typeof v === "undefined" || v === "")
    );

    if (isAnyValueUndefined) {
      Swal.fire({ icon: "warning", title: "Please add all mandatory fields" });
      setIsLoading(false);
      return;
    }
    try {
      if (inputs.call_mode == "mobile" && id != "0") {
        const response = await axiosprivate.post(
          "/contactcentre/check_agent_in_any_whatsapp_campaign/",
          {
            "user_id": user_id,
            "agent_id": id
          },
          { signal: controller.signal }
        );
        if (response.data?.is_in_campaign) {
          const campaigns = response.data.campaign_names || [];

          Swal.fire({
            icon: "warning",
            title: "Agent is already assigned",
            html: `
              <div style="text-align:center;">
                <p style="margin:0 0 6px; color:#555; font-size:14px;">This agent still exists in the following WhatsApp campaign(s).</p>
                <p style="margin:0 0 12px; color:#555; font-size:14px;">Please remove the agent from these campaigns before proceeding.</p>
                <ul style="
                  display:inline-block;
                  text-align:left;
                  margin:0 auto;
                  padding:10px 20px;
                  background:#fff8e1;
                  border:1px solid #ffe082;
                  border-radius:8px;
                  list-style:none;
                  width:100%;
                  box-sizing:border-box;
                ">
                  ${campaigns.map(name => `
                    <li style="
                      padding:6px 0;
                      border-bottom:1px solid #ffe082;
                      font-size:13px;
                      color:#5d4037;
                      display:flex;
                      align-items:center;
                      gap:8px;
                    ">
                      <span style="color:#f9a825; font-size:16px;">&#9679;</span>${name}
                    </li>
                  `).join("")}
                </ul>
              </div>
            `,
            confirmButtonText: "OK",
            confirmButtonColor: "#f9a825",
            customClass: {
              popup: "swal-campaign-popup",
              title: "swal-campaign-title",
            },
            width: "420px",
          });
          setIsLoading(false);
          return;
        }
      }


    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: err.response?.data?.message || "Something went wrong.",
      });
      setIsLoading(false);
      console.error(err);
    }

    try {
      const response = await axiosprivate.post(
        "/contactcentre/agents/create/",
        post_data,
        { signal: controller.signal }
      );

      if (response.data.type === "sucess") {
        Swal.fire({ icon: "success", title: response.data.message });
        navigate("/admin/list/agent");
        setIsLoading(false);
      } else if (response.data.code === 1) {
        Swal.fire({
          icon: "warning",
          title: response.data.message,
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Continue",
        }).then((result) => {
          if (result.isConfirmed) {
            handleSubmit({ preventDefault: () => { } }, "yes");
          }
          setIsLoading(false);
        });
      } else if (response.data.status === "whatsapp_campaign_error") {
        Swal.fire({
          icon: "error",
          title: "Cannot Change Call Mode",
          text: response.data.message,
        });
        setIsLoading(false);
      } else {
        Swal.fire({ icon: "warning", title: response.data.message });
        setIsLoading(false);
      }
    } catch (err) {
      Swal.fire({
        icon: "warning",
        title: err.response?.data?.message || "Something went wrong.",
      });
      console.error(err);
      setIsLoading(false);
    }
  };

  const labelCls = "block text-sm font-medium text-slate-600 mb-2";
  const inputCls =
    "w-full h-12 px-4 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 hover:border-slate-300";
  const errorCls = "text-red-500 text-xs mt-1.5";

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full p-4 md:p-6"
      autoComplete="off"
    >
      {/* 🛡 Hidden dummy fields to defeat Chrome autofill */}
      <input
        type="text"
        name="fakeusernameremembered"
        autoComplete="username"
        style={{ display: "none" }}
      />
      <input
        type="password"
        name="fakepasswordremembered"
        autoComplete="new-password"
        style={{ display: "none" }}
      />

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 w-full">
        {/* Header */}
        <div className="mb-8 pb-6 border-b border-slate-100">
          <h2 className="text-2xl font-semibold text-slate-800">
            {isEditMode ? "Edit Agent" : "Add New Agent"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure agent credentials, call mode and WebRTC settings.
          </p>
        </div>

        {/* Main responsive grid — now fills the screen */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* User Type */}
          <div>
            <label className={labelCls}>User Type</label>
            <select
              name="user_type"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              className={inputCls}
            >
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Username / Admin Name */}
          <div>
            <label className={labelCls}>
              {isAdminType ? "Admin Name" : "User Name"}
            </label>
            <input
              type="text"
              name="agent_username"
              value={inputs.agent_username || ""}
              onClick={() => Validation("agent_username")}
              onChange={handleChange}
              readOnly={isAdminType}
              className={isAdminType ? inputCls + " bg-slate-50" : inputCls}
              placeholder={isAdminType ? "Enter admin name" : "Enter user name"}
              autoComplete="off"
              required
            />
            {required.username && (
              <div className={errorCls}>
                {isAdminType ? "*Please Enter Admin Name" : "*Please Enter Username"}
              </div>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className={labelCls}>Phone Number</label>
            <input
              type="text"
              name="phone_number"
              value={inputs.phone_number || ""}
              onClick={() => Validation("phone_number")}
              onChange={handleChange}
              readOnly={isAdminType}
              className={isAdminType ? inputCls + " bg-slate-50" : inputCls}
              placeholder="10-digit phone number"
              autoComplete="off"
              required
            />
            {required.phone_number && <div className={errorCls}>{error}</div>}
          </div>

          {/* Call Mode */}
          <div>
            <label className={labelCls}>Call Mode</label>
            <select
              name="call_mode"
              value={inputs.call_mode || ""}
              onChange={handleChange}
              className={inputCls}
            >
              <option value="">Select call mode</option>
              <option value="mobile">Mobile</option>
              <option value="sip">SIP</option>
            </select>
            {required.call_mode && (
              <div className={errorCls}>*Please Choose a Call Mode</div>
            )}
          </div>

          {/* Call Option */}
          <div>
            <label className={labelCls}>Call Option</label>
            <select
              name="call_option"
              value={inputs.call_option || ""}
              onChange={handleChange}
              className={inputCls}
            >
              <option value="">Select call option</option>
              <option value="OUTGOING">Outgoing</option>
            </select>
            {required.call_option && (
              <div className={errorCls}>*Please Choose a Call Option</div>
            )}
          </div>

          {/* Role */}
          <div>
            <label className={labelCls}>Role</label>
            <select
              name="user_role"
              value={inputs.user_role || ""}
              onFocus={fetchRoles}
              onChange={handleChange}
              className={inputCls}
            >
              <option value="">Select role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            {required.user_role && (
              <div className={errorCls}>*Please Choose a Role</div>
            )}
          </div>

          {/* Password */}
          <div>
            <label className={labelCls}>Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="agent_password"
                value={inputs.agent_password || ""}
                onClick={() => Validation("agent_password")}
                onChange={handleChange}
                className={inputCls + " pr-12"}
                placeholder="Enter password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {required.password && (
              <div className={errorCls}>*Please Enter Password</div>
            )}
          </div>

          <div>
            <label className={labelCls}>DID</label>
            <select
              name="did"
              value={inputs.did || ""}
              onFocus={fetDIDData}
              onChange={handleChange}
              className={inputCls}
            >
              <option>{inputs.did}</option>
              {did.map((obj, index) => (
                <option value={obj.name}>{obj.name}</option>
              ))}
            </select>
            {required.did && (
              <div className={errorCls}>*Please Choose a Call Option</div>
            )}
          </div>

          {/* Login Activation */}
          <div>
            <label className={labelCls}>Login Activation</label>
            <select
              name="login_activation"
              value={inputs.login_activation || "Allowed"}
              onChange={handleChange}
              className={inputCls}
            >
              <option value="Allowed">Allowed</option>
              <option value="Not Allowed">Not Allowed</option>
            </select>
          </div>

          {/* VSIP Password */}
          {inputs.call_mode === "sip" && (
            <div>
              <label className={labelCls}>VSIP Password</label>
              <div className="relative">
                <input
                  readOnly
                  type="text"
                  name="vsipobj_pass"
                  value={inputs.vsipobj_pass || ""}
                  className={inputCls + " pr-12 bg-slate-50"}
                  placeholder="Auto-generated"
                  required
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-700 hover:rotate-180 transition-transform duration-300"
                  title="Regenerate password"
                >
                  <RefreshCw size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Allowed IP */}
          {inputs.call_mode === "sip" && (
            <div>
              <label className={labelCls}>Allowed IP Address</label>
              <input
                type="text"
                name="allowed_ip"
                value={inputs.allowed_ip || "all"}
                onChange={handleChange}
                className={inputCls}
                placeholder="Allowed IP Address"
                required
              />
            </div>
          )}
        </div>

          {/* Feedback required before next call */}
          <div className="bg-white rounded-2xl pt-6 pr-6 md:pt-8 md:pr-8 w-full">
            <div className="flex flex-wrap gap-6 items-center">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={feedbackRequiredBeforeNextCall}
                    onChange={() => setFeedbackRequiredBeforeNextCall(!feedbackRequiredBeforeNextCall)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
                  <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  EnableFeedback Required Before Next Call
                </span>
              </label>
            </div>
          </div>

        {/* WebRTC section */}
        {inputs.call_mode === "sip" && (
          <div className="mt-8 p-6 bg-slate-150 rounded-2xl border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">
              WebRTC Settings
            </h3>

            <div className="flex flex-wrap gap-6 items-center">
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={webrtc}
                    onChange={() => setWebRtc(!webrtc)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
                  <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
                </div>
                <span className="text-sm font-medium text-slate-700">
                  Enable WebRTC
                </span>
              </label>

              {webrtc && (
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={autoAnswer}
                      onChange={() => setAutoAnswer(!autoAnswer)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5"></div>
                  </div>
                  <span className="text-sm font-medium text-slate-700">
                    Auto Answer
                  </span>
                </label>
              )}

              {webrtc && autoAnswer && (
                <div className="flex items-center gap-3">
                  <label
                    htmlFor="autoAnswerInterval"
                    className="text-sm font-medium text-slate-700"
                  >
                    Auto Answer After
                  </label>
                  <div className="relative">
                    <input
                      id="autoAnswerInterval"
                      type="number"
                      min="1"
                      max="60"
                      value={autoAnswerInterval}
                      onChange={(e) => setAutoAnswerInterval(e.target.value)}
                      className="w-24 h-10 px-3 pr-12 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 text-center focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">
                      sec
                    </span>
                  </div>
                </div>
              )}
            </div>

            {webrtc && autoAnswer && (
              <p className="mt-3 text-xs text-slate-500">
                Incoming calls will be answered automatically after{" "}
                <span className="font-semibold text-slate-700">
                  {autoAnswerInterval || 0}
                </span>{" "}
                second{autoAnswerInterval == 1 ? "" : "s"}.
              </p>
            )}
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end mt-8 pt-6 border-t border-slate-100">
          <button
            type="submit"
            disabled={isLoading}
            className="px-8 h-12 rounded-full text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
            style={{
              backgroundImage:
                "linear-gradient(177.88deg, #AFD462 1.79%, #6BC698 49.97%, #0FB1E2 82.06%)",
            }}
          >
            {isEditMode ? "Update Agent" : "Save Agent"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddUser;